import { Injectable } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {concatMap, of} from 'rxjs';
import {switchMap, map, catchError, withLatestFrom, filter} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {BuildKitService} from "../../data-access/build-kit.service";
import {AppState} from "../../../shared/state/app.state";
import {BuildKitPartNumber} from "../../../shared/models/build-kit.model";
import {arraysEqual} from "../../../shared/utils/array.utils";
import {
  loadPartNumberDrawingBlob,
  loadPartNumberDrawingBlobFailure,
  loadPartNumberDrawingBlobSuccess,
  loadPartNumbers,
  loadPartNumbersEmpty,
  loadPartNumbersFailure,
  loadPartNumbersSuccess
} from "./build-kit-part-number.action";
import {DrawingService} from "../../../drawing/data-access/drawing.service";
import {selectAllBuildKitPartNumbers, selectPartDrawingBlobMap} from "./build-kit-part-number.selectors";


@Injectable()
export class BuildKitPartNumberEffects {
  loadPartNumberDrawingBlob = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPartNumberDrawingBlob),
      withLatestFrom(this.store.select(selectPartDrawingBlobMap)),
      filter(([action, blobDrawingMap]) => {
        if (!(action.pdfDrawingFileKey in blobDrawingMap)){
          return true;
        }

        return blobDrawingMap[action.pdfDrawingFileKey].inProgress === true && blobDrawingMap[action.pdfDrawingFileKey].previousCallInProgress === false && blobDrawingMap[action.pdfDrawingFileKey].completed === false;
      }),
      concatMap(([action, blobDrawingMap]) =>
        this.drawingService.downloadByPdfDrawingKey(action.pdfDrawingFileKey)
          .pipe(
            map((blob) => loadPartNumberDrawingBlobSuccess({pdfDrawingFileKey: action.pdfDrawingFileKey, blob })),
            catchError((error) => of(loadPartNumberDrawingBlobFailure({ error })))
          )
      )
    )
  );


  loadPartNumbers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPartNumbers),
      withLatestFrom(this.store.select(selectAllBuildKitPartNumbers)),
      switchMap(([action, buildKitPartNumbers]) =>{
          if (action.cadModelRevisionIds.length < 1){
            return of(loadPartNumbersEmpty())
          }

          const existingBuildKitPartNumbers = buildKitPartNumbers.find((buildKitPartNumber: BuildKitPartNumber) =>
            arraysEqual(
              buildKitPartNumber.cadModelRevisionIds.filter(x => x != null && x != ''),
              action.cadModelRevisionIds.filter(x => x != null && x != '')
            )
          );

          if (existingBuildKitPartNumbers){
            return of(loadPartNumbersSuccess({
              buildKitPartNumber:
                {
                  buildKitId: action.buildKitId,
                  cadModelRevisionIds: action.cadModelRevisionIds,
                  partNumbers: existingBuildKitPartNumbers.partNumbers
                } as BuildKitPartNumber
            }));
          }
          return this.buildKitService.getPartsFromCadModelIds(action.cadModelRevisionIds)
            .pipe(
              map((partNumbers) => {
                  return loadPartNumbersSuccess({
                    buildKitPartNumber:
                      {
                        buildKitId: action.buildKitId,
                        cadModelRevisionIds: action.cadModelRevisionIds,
                        partNumbers: partNumbers
                      } as BuildKitPartNumber
                  })
                }
              ),
              catchError((error) => of(loadPartNumbersFailure({ error })))
            )
        }
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private buildKitService: BuildKitService,
    private drawingService: DrawingService,
  ) {}
}

